<template>
  <checkout-section-card>
    <h2
      class="mb-5 flex items-center gap-2.5 text-xl font-semibold uppercase tracking-wide text-mouse"
    >
      <fa-icon :icon="faCalendarClock" class="size-5 text-rhino" />
      {{ sectionTitle }}
    </h2>

    <!-- Skeleton while data is loading -->
    <div
      v-if="checkoutStore.isLoading && checkoutStore.durations.length === 0"
      class="flex flex-wrap gap-3 animate-pulse"
    >
      <div
        v-for="i in 4"
        :key="i"
        class="h-10 w-24 rounded-lg bg-depth-2"
      ></div>
    </div>

    <!-- A failed fetch also leaves durations empty -->
    <p
      v-else-if="checkoutStore.durations.length === 0 && !checkoutStore.error"
      class="text-rhino"
    >
      {{ t('checkout/configure/duration/empty') }}
    </p>
    <p v-else-if="checkoutStore.durations.length === 0" class="text-danger-fg">
      {{ t('checkout/error/order_info') }}
    </p>

    <!-- Actual buttons -->
    <div v-else class="flex flex-wrap gap-3">
      <button
        v-for="duration in checkoutStore.durations"
        :key="duration"
        type="button"
        data-testid="duration-option"
        :data-selected="duration === checkoutStore.selectedDuration"
        class="relative rounded-lg border-2 px-4 py-2 transition-all duration-200"
        :class="[
          optionCardClass(duration === checkoutStore.selectedDuration),
          duration === checkoutStore.selectedDuration
            ? 'text-brand'
            : 'text-mouse',
        ]"
        @click="onSelect(duration)"
      >
        {{ formatDuration(duration) }}

        <span
          v-if="discountForDuration(duration)"
          class="absolute -top-3 -right-2 rounded-full bg-success px-2 py-0.5 text-sm font-bold leading-tight text-onyx"
        >
          {{ discountForDuration(duration) }}
        </span>
      </button>
    </div>
  </checkout-section-card>
</template>

<script setup lang="ts">
import { faCalendarClock } from '@fortawesome/pro-solid-svg-icons';
import {
  DURATION_DISCOUNTS,
  SECONDS_PER_DAY,
  optionCardClass,
} from '~/helpers/checkout';

const { t } = useI18n();
const checkoutStore = useCheckoutStore();

const sectionTitle = computed(() => checkoutStore.summaryDurationLabel);
const SECONDS_30_DAYS = 30 * SECONDS_PER_DAY;

watch(
  () => checkoutStore.durations,
  (durations) => {
    if (checkoutStore.selectedDuration !== null || durations.length === 0)
      return;

    const thirtyDay = durations.find((d) => d === SECONDS_30_DAYS);
    checkoutStore.selectDuration(thirtyDay ?? durations[0]);
  },
  { immediate: true },
);

function formatDuration(seconds: number): string {
  const days = Math.round(seconds / SECONDS_PER_DAY);
  return t('checkout/configure/duration/days', { count: days });
}

function discountForDuration(seconds: number): string | null {
  const pct = DURATION_DISCOUNTS[seconds];
  return pct ? `-${pct}%` : null;
}

function onSelect(durationSeconds: number): void {
  checkoutStore.selectDuration(durationSeconds);
}
</script>
